import { Component, OnInit } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { concatMap, switchMap, take, tap } from 'rxjs/operators';
import { AccountUser } from '../../../../models/account-user.model';
import { Account } from '../../../../models/ui/account.model';
import { LexbeCase } from '../../../../models/case.model';
import { HelpService } from '../../../../services/help.service';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { ManageUserCreateComponent } from '../manage-user-create/manage-user-create.component';
import { AccountAdminService } from '../../../../services/account-admin.service';
import { AccountUserTypes } from '../../../../models/account-user-type';
import { EMPTY } from 'rxjs';
import { NotificationManager } from '../../../../managers/notification.manager';

@Component({
    selector: 'lex-manage-user-list',
    templateUrl: './manage-user-list.component.html',
    styleUrls: ['./manage-user-list.component.scss'],
    standalone: false
})
export class ManageUserListComponent implements OnInit {

	public selectedAccount: Account = null;
	public selectedAccountId: string = null;
	public users: AccountUser[] = [];
	public sortedData: AccountUser[] = [];
	public selectedUser: AccountUser = null;
	public cases: LexbeCase[] = [];
	public selectedCases: string[] = [];
	public accountUserTypes = new AccountUserTypes();

	public hoverFieldId: string = null;

	constructor(
		private dialog: MatDialog,
		private notificationManager: NotificationManager,
		private accountAdminService: AccountAdminService,
		private helpService: HelpService,
		private activatedRoute: ActivatedRoute) { }

	ngOnInit(): void {
		this.helpService.updateHelpLink('account-menu/manage-users');
		this.activatedRoute.queryParams.pipe(take(1)).subscribe(
			param => {
				if (param['accountId']) {
					this.selectedAccountId = param['accountId'];
				}
			});
	}

	public onMouseOverRow(user: AccountUser): void {
		this.hoverFieldId = user.userId;
		this.selectedCases = this.cases.filter(x => user.caseIds.includes(x.caseId)).map(x => x.caseName);
	}

	public onMouseOutRow(): void {
		this.hoverFieldId = null;
		this.selectedCases = [];
	}

	public sortData(sort: Sort) {
		const data = this.users.slice();
		if (!sort.active || sort.direction === '') {
			this.sortedData = data;
			return;
		}


		this.sortedData = data.sort((a, b) => {
			const isAsc = sort.direction === 'asc';
			switch (sort.active) {
				case 'userName': return compare(a.userName, b.userName, isAsc);
				case 'userType': return compare(a.userType, b.userType, isAsc);
				case 'caseCount': return compare(a.caseIds.length, b.caseIds.length, isAsc);
				case 'twoFactorEnabled': return compare(a.twoFactorEnabled, b.twoFactorEnabled, isAsc);
				default: return 0;
			}
		});
	}

	public selectAccount(): void {
		this.selectedUser = null;
		this.selectedAccountId = null;

		if (this.selectedAccount) {
			this.accountAdminService.getAccountUsers(this.selectedAccount.accountId).pipe(
				tap(data => {
					this.users = data;
					this.sortedData = this.users.slice();
				}),
				concatMap(() => this.accountAdminService.getCases(this.selectedAccount.accountId))
			).subscribe(data => {
				this.cases = data;
			});
		}
		else {
			this.users = [];
			this.cases = [];
		}
	}

	public clearSelectedUser(getAccountUserlist: boolean): void {
		this.selectedUser = null;
		this.selectAccount();
		if (getAccountUserlist) {
			this.notificationManager.displaySnackBarMessage({ message: 'User updated for account successfully.', ttl: 10000 });
		}
	}

	public addUser(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		this.dialog.open(ManageUserCreateComponent, dialogConfig).afterClosed().pipe(
			switchMap((user) => {
				if (user) {
					user.accountName = this.selectedAccount.accountName;
					return this.accountAdminService.createUserInAccount(this.selectedAccount.accountId, user);
				}
				else
					return EMPTY;
			})).subscribe((userName: string) => {
				this.selectAccount();
				this.notificationManager.displaySnackBarMessage({ message: 'User added to account successfully. Email sent to ' + userName + '.', ttl: 10000 });
			}, () => {
				this.notificationManager.displaySnackBarMessage({ message: 'An error has occurred. Please contact Professional Service for support.', type: 'error' });
			});
	}

	public removeUser(event: any, userId: string): void {
		event.stopPropagation();
		event.preventDefault();
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Remove User From Account`,
			message: `Completely Remove User From Account? <br/>
          The user will no longer have access to any cases in this account <br/ >
          and will no longer be able to login to this account.`
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().pipe(
			switchMap((okClicked: boolean) => {
				if (okClicked) {
					return this.accountAdminService.removewUserFromAccount(this.selectedAccount.accountId, userId);
				}
			})).subscribe(() => {
				this.selectAccount();
				this.notificationManager.displaySnackBarMessage({ message: 'User removed from account successfully.', ttl: 10000 });
		});
	}

}

function compare(a: number | string | boolean, b: number | string | boolean, isAsc: boolean) {
	return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
